import React from 'react';
import styled from 'styled-components';

import * as S from './RoomSelection.styles';

import {useSelector} from 'react-redux';

import Room from './Room';
import {Row} from '../../../../../stories/components/hotels/common/Layouts/Layouts';
import TableHeader from '../../../../../stories/components/hotels/DetailsPage/RoomSelectionHeader/RoomSelectionHeader';
import RoomFlavorLoader from '../../../../../stories/components/hotels/Loadingpage/RoomTypeLoader/RoomTypeLoader';

import {
  indexedRoomsStaticDataSelector,
  isHermesLoading as isHermesLoadingSelectorFn,
  isStaticDataLoading as isStaticDataLoadingSelectorFn,
  hasHermesDataBooleanSelector,
} from '../../selectors';

import {RplType, StaticRoomInfo} from '../../redux/types';

import {detailsNavigationIds} from '../../details.config';

const RoomsStyledContainer = styled(Row)(
  props => `
  // border: 0.1rem solid ${props.theme.colors.blue.bgBlue};
  // background-color: white;
  // padding: ${props.theme.spacing.large.l20};
  border-radius: ${props.theme.misc.borderRadius.base};
  margin: ${props.theme.spacing.large.l20} 0 0 0;
`,
);

const NoRoomsAvailableContainer = styled.div(
  props => `
    padding: ${props.theme.spacing.large.l20};
  `,
);

const NoRoomsAvailableText = styled.div(
  props => `
    color: ${props.theme.colors.goBrand.blue}
    font-size: ${props.theme.font.large.font16}
    font-weight: bold;
  `,
);

const NoRoomsAvailable = () => (
  <NoRoomsAvailableContainer>
    <NoRoomsAvailableText>No Rooms Available</NoRoomsAvailableText>
  </NoRoomsAvailableContainer>
);

const getSlashAndShowPrice = (roomFlavor: RplType) => {
  const payMode = roomFlavor.pm || 1;
  // const payAtHotel = payMode === 2 || payMode === 4 || payMode === 5 || payMode === 3;

  const payAtHotelPaymode = payMode === 2 || payMode === 4;
  const payAtHotelCCRequired = payMode === 3;
  const payAtHotelReserveNowPayLater = payMode === 5;

  const payAtHotel = payAtHotelPaymode || payAtHotelCCRequired || payAtHotelReserveNowPayLater;
  const payAtHotelShowPrice0 = payAtHotelPaymode || payAtHotelReserveNowPayLater;

  // const slashedPrice = payAtHotel ? 0 : roomFlavor.pd?.opr;
  const slashedPrice = roomFlavor.pd?.dspr;

  // const showPrice = payAtHotelShowPrice0 ? 0 : roomFlavor.pd?.spr;
  const showPrice = roomFlavor.pd?.spr;

  // const payAtPersuasionShowPrice = payAtHotelShowPrice0 ? roomFlavor.pd?.spr : 0;
  const payAtPersuasionShowPrice = roomFlavor.pd?.spr;

  return {slashedPrice, showPrice, payAtPersuasionShowPrice};
};

function getStaticDataForThisRoom({
  indexedRoomsStaticData,
  roomCode,
}: {
  indexedRoomsStaticData: {
    [K: number]: StaticRoomInfo;
  };
  roomCode: string;
}) {
  return indexedRoomsStaticData[roomCode] || {type_codes: [], type_code: ''};
}

// 🐨 this component should re render only twice, right now it's happening more than that
const Rooms = ({rooms = [], roomsFromHermes = []}) => {

  const indexedRoomsStaticData = useSelector(indexedRoomsStaticDataSelector);

  const isHermesLoading = useSelector(isHermesLoadingSelectorFn);
  const isStaticDataLoading = useSelector(isStaticDataLoadingSelectorFn);

  const isDataLoading = isHermesLoading || isStaticDataLoading;
  const hasHermesData = useSelector(hasHermesDataBooleanSelector);

  const noInclusionsAndSplInstructionsForAnyRoom = !rooms.some(room => {
    return (room.rpl || []).some(roomFlavor => {
      return (
        (roomFlavor.am && Array.isArray(roomFlavor.am) && !!roomFlavor.am.length) ||
        (roomFlavor.ex && Array.isArray(roomFlavor.ex) && !!roomFlavor.ex.length)
      );
    });
  });

  if (!hasHermesData) {
    return (
      <RoomsStyledContainer id={detailsNavigationIds.rooms}>
        <RoomFlavorLoader />
      </RoomsStyledContainer>
    );
  }

  /** if there are rooms from hermes, but filters applied doesn't want to show any room */
  if (!isHermesLoading && hasHermesData && !!roomsFromHermes.length && !rooms.length) {
    return <NoRoomsAvailable />;
  }

  if (!rooms.length) {
    return null;
  }

  return (
    <S.RoomSelectOuterWrap id={detailsNavigationIds.rooms}>
      <>
        {!!rooms.length && (
          <TableHeader
            noInclusionsAndSplInstructionsForAnyRoom={noInclusionsAndSplInstructionsForAnyRoom}
          />
        )}

        {rooms.map((room, index) => {
          const roomCode = room.rtc;
          const roomGuestsString = room.gs;

          const staticDataForThisRoom: typeof indexedRoomsStaticData[0] = getStaticDataForThisRoom({
            indexedRoomsStaticData,
            roomCode,
          });

          const roomFlavorsDataForThisRoom = room.rpl;

          const neededRoomFlavorsData = roomFlavorsDataForThisRoom.map(roomFlavor => {
            const {slashedPrice, showPrice, payAtPersuasionShowPrice} = getSlashAndShowPrice(
              roomFlavor,
            );

            const ret = {
              roomFlavorName: roomFlavor.rtn,
              refundStatus: (roomFlavor.fcp?.fct === 'fc' || roomFlavor.fcp?.fct === 'fcc'),
              refundStatusText: roomFlavor.cltxt,
              refundStatusTextColor: roomFlavor.cltxtc,
              amenities: roomFlavor.am,
              exclusions: roomFlavor.ex,
              isRecommended: !!roomFlavor.rwt,
              slashedPrice,
              showPrice,
              showSlashedPrice: !!roomFlavor?.pd?.do,
              payAtPersuasionShowPrice,
              uid: roomFlavor.grpc,
              paxString: roomFlavor.pd?.pax || '',
              emiData: {
                emiStartingPrice: roomFlavor.pd?.emi,
                emiRates: roomFlavor.pd?.emid,
              },

              tax: roomFlavor.pd?.tnc,

              forwardParams: roomFlavor.fwdp,
              roomPriceCode: roomFlavor.rpc,
              offercode: roomFlavor.offercode,
              vendor: roomFlavor.ibp,
              paymode: roomFlavor.pm,
              roomTypeCode: roomFlavor.rtc,

              gtv: roomFlavor.gtv,
              gseg: roomFlavor.gseg,
              cot: roomFlavor.cot,
              hmd: roomFlavor.hmd,
              gsv: roomFlavor.gsv,
              pc: roomFlavor.pc, // promo code
              dspr: roomFlavor.pd?.dspr,
              roomGuestsString,
              cbo: roomFlavor.pd?.cbo
            };

            return ret;
          });

          const roomProps = {
            roomTypeColumnProps: {
              maxNoOfAdults: staticDataForThisRoom.max_occ,
              roomSize: staticDataForThisRoom.roomsize,
              bedType: staticDataForThisRoom.bedtype,
              imageUrl: staticDataForThisRoom.image,
              roomTypeName: room.rtn,
              extraBedType: staticDataForThisRoom.extra_bedtype,
              roomDescription: staticDataForThisRoom.description,
              roomFacilities: staticDataForThisRoom.facilities,
              roomImages: (staticDataForThisRoom.gallery || []).map(imageData => {
                return imageData[2];
              }),
              noInclusionsAndSplInstructionsForAnyRoom: noInclusionsAndSplInstructionsForAnyRoom,
            },
            roomFlavorsList: neededRoomFlavorsData,
          };

          return (
            <div key={`${roomCode}-${index}`}>
              <Room
                key={`${roomCode}-${index}`}
                noInclusionsAndSplInstructionsForAnyRoom={noInclusionsAndSplInstructionsForAnyRoom}
                {...roomProps}
              />
              {(index !== rooms.length - 1 && <S.RoomDivider />) || null}
            </div>
          );
        })}
      </>
      
    </S.RoomSelectOuterWrap>
  );
};

export default Rooms;
